import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Form } from '@/components/molecules/form-field';
import { FormPageHeader, FormProgressIndicator } from '@/components/forms';
import { LimitReachedAlert, LimitIndicator } from '@/lib/subscription';

import {
  ShopBasicInfo,
  ShopLocation,
  ShopSchedule,
  ShopShippingRates,
  ShopImages,
  useShopImagesEditor,
  useCreateShop,
  createShopSchema,
  type CreateShopFormData,
} from '@/features/shops';

import { useShippingRates } from '@/features/shipping/hooks/use-shipping-rates';
import { useCountries } from '@/lib/hooks/use-countries';
import { useCanCreateShop, useSubscriptionLimits } from '@/lib/subscription';
import { DEFAULT_AVAILABILITY, DEFAULT_DISABLED } from '@/lib/constants/form-options';
import type { Country } from '@/lib/types';

export default function NewShopPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: countries = [] } = useCountries();
  const { data: shippingRatesData = [], isLoading: isLoadingShippingRates } = useShippingRates();
  const { mutate: createShop, isPending: isSubmitting } = useCreateShop();

  // Subscription gating
  const shopAccess = useCanCreateShop();
  const limits = useSubscriptionLimits();

  // Single source of truth for images
  const {
    images: allImages,
    previews,
    newFiles,
    isCompressing,
    addImages,
    removeImage,
    reorderImages,
  } = useShopImagesEditor();

  const form = useForm<CreateShopFormData>({
    resolver: zodResolver(createShopSchema),
    defaultValues: {
      name: '',
      manager: '',
      description: '',
      phoneNumber: '',
      delivery: 'both',
      address: '',
      city: '',
      department: '',
      postalCode: '',
      countryIsoCode3: '',
      latitude: undefined,
      longitude: undefined,
      tags: '',
      shippingRates: [],
      monday: DEFAULT_AVAILABILITY,
      tuesday: DEFAULT_AVAILABILITY,
      wednesday: DEFAULT_AVAILABILITY,
      thursday: DEFAULT_AVAILABILITY,
      friday: DEFAULT_AVAILABILITY,
      saturday: { ...DEFAULT_AVAILABILITY, startDateMorning: '09:00', closeDateMorning: '13:00', startingDateAfternoon: '', closeDateAfternoon: '' },
      sunday: DEFAULT_DISABLED,
    },
  });

  const formValues = form.watch();

  // Calculate form progress
  const { completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['name', 'manager', 'description', 'phoneNumber', 'delivery', 'countryIsoCode3', 'city', 'address'];

    let completed = requiredFields.filter((field) => {
      const value = formValues[field as keyof CreateShopFormData];
      return value !== '' && value !== undefined;
    }).length;

    if (allImages.length > 0) completed++;
    if (formValues.shippingRates && formValues.shippingRates.length > 0) completed++;
    const total = requiredFields.length + 2;

    return { completedFields: completed, totalFields: total };
  }, [formValues, allImages.length]);

  const onSubmit = (data: CreateShopFormData) => {
    if (allImages.length === 0) {
      toast.error(t('shops.imagesRequired'));
      return;
    }

    const selectedCountry = countries.find((c: Country) => c.isoCode3 === data.countryIsoCode3);

    const addressObj = {
      address_line1: data.address,
      address_line2: '',
      country: {
        name: selectedCountry?.name || data.countryIsoCode3,
        isoCode3: data.countryIsoCode3?.toUpperCase(),
        countryIsoCode3: data.countryIsoCode3?.toUpperCase(),
      },
      city: data.city,
      state_province: data.department || '',
      postal_code: data.postalCode || '',
    };

    const shopData = {
      name: data.name,
      manager: data.manager,
      description: data.description,
      phone: data.phoneNumber,
      delivery: data.delivery,
      address: JSON.stringify(addressObj),
      city: data.city,
      shippingRates: data.shippingRates,
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday,
      saturday: data.saturday,
      sunday: data.sunday,
    } as any;

    createShop(
      { data: shopData, images: newFiles },
      {
        onSuccess: () => {
          toast.success(t('shops.shopCreated'));
          navigate('/shops');
        },
        onError: (error) => {
          console.error('Failed to create shop:', error);
          toast.error(t('common.error'));
        },
      }
    );
  };

  // Limit reached view
  if (!shopAccess.canCreate) {
    return (
      <div className="space-y-6">
        <FormPageHeader
          backLink="/shops"
          title={t('shops.addShop')}
          description={t('shops.addShopDescription')}
        />
        <LimitReachedAlert
          type="shop"
          limit={shopAccess.limit}
          onUpgrade={() => navigate('/settings/subscription')}
        />
      </div>
    );
  }

  // Handle image removal
  const handleImageRemove = (_index: number, imageId?: number | string) => {
    if (imageId !== undefined) {
      removeImage(imageId);
    }
  };

  return (
    <div className="pb-6">
      <FormPageHeader
        backLink="/shops"
        title={t('shops.addShop')}
        description={t('shops.addShopDescription')}
      />

      <LimitIndicator
        current={limits.shops.current}
        max={limits.shops.limit}
        label={t('subscription.features.shops')}
      />

      <FormProgressIndicator
        completed={completedFields}
        total={totalFields}
        label={t('shops.fieldsCompleted')}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Images - first on mobile */}
            <div className="order-first lg:order-last space-y-6">
              <ShopImages
                previews={previews}
                onAdd={addImages}
                onRemove={handleImageRemove}
                onReorder={reorderImages}
                isCompressing={isCompressing}
              />

              <div className="hidden lg:block sticky bottom-4">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('shops.createShop')}
                </Button>
              </div>
            </div>

            {/* Form fields */}
            <div className="lg:col-span-2 space-y-6">
              <ShopBasicInfo form={form} />
              <ShopLocation form={form} countries={countries} />
              <ShopShippingRates
                form={form}
                shippingRates={shippingRatesData}
                isLoading={isLoadingShippingRates}
              />
              <ShopSchedule form={form} />

              {/* Submit button - visible on mobile only */}
              <div className="lg:hidden">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('shops.createShop')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
